import { supabase } from './supabase'
import { isInstalled, isIOS, getIOSVersion, supportsWebPush } from './platform'

/**
 * Web Push subscription management.
 *
 * The iOS rules are strict and unforgiving, and every one of them is a way
 * this silently fails:
 *
 *  · the app must be installed to the home screen (16.4+). Not "should" —
 *    Safari will not even offer the permission prompt otherwise.
 *  · permission must be requested from a real user gesture. Asking on load
 *    throws, and worse, a denied permission cannot be re-asked in-app.
 *  · subscriptions expire without warning, so we re-subscribe on every launch
 *    rather than trusting the stored one.
 */

export type PushStatus =
  | 'unsupported'
  | 'needs_install'
  | 'ios_too_old'
  | 'default'
  | 'granted'
  | 'denied'

export function pushStatus(): PushStatus {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported'

  if (isIOS()) {
    const v = getIOSVersion()
    if (v && (v[0] < 16 || (v[0] === 16 && v[1] < 4))) return 'ios_too_old'
    if (!isInstalled()) return 'needs_install'
  }
  if (!supportsWebPush()) return 'needs_install'

  return Notification.permission as PushStatus
}

/**
 * Ask for permission and register. MUST be called from a click handler —
 * iOS rejects it otherwise, and a rejection is not recoverable in-app.
 */
export async function enablePush(userId: string): Promise<PushStatus> {
  const status = pushStatus()
  if (status !== 'default' && status !== 'granted') return status

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission as PushStatus

  await registerSubscription(userId)
  return 'granted'
}

/**
 * Re-subscribe and store. Safe to call on every launch — the unique constraint
 * on (user_id, endpoint) makes it idempotent, and this is what quietly repairs
 * a subscription the push service has rotated.
 */
export async function registerSubscription(userId: string): Promise<void> {
  if (Notification.permission !== 'granted') return

  const publicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined
  if (!publicKey) {
    console.warn('No VAPID public key configured — push cannot be registered.')
    return
  }

  const registration = await navigator.serviceWorker.ready

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    })
  }

  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return

  await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      user_agent: navigator.userAgent.slice(0, 200),
      last_seen_at: new Date().toISOString(),
      failed_at: null,
    },
    { onConflict: 'user_id,endpoint' },
  )
}

export async function disablePush(userId: string): Promise<void> {
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (subscription) {
    await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId)
      .eq('endpoint', subscription.endpoint)
    await subscription.unsubscribe()
  }
}

/** VAPID keys are base64url; PushManager wants raw bytes. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalised)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

/**
 * Prove it, rather than assume it.
 *
 * Four things sit between "enabled" and a notification arriving: iOS
 * permission, a subscription stored on the server, VAPID keys that match, and
 * a worker that delivers. Each fails silently. The only way to know they work
 * together is to push one through — and finding out during a real event means
 * finding out at the moment it mattered.
 */
export async function sendTestNotification(): Promise<void> {
  const { error } = await supabase.rpc('send_test_notification')
  if (error) {
    if (error.message.includes('niet aangemeld')) {
      throw new Error(
        'Dit toestel staat nog niet aangemeld. Zet meldingen aan, of open de app ' +
          'vanaf je beginscherm — in Safari zelf werkt het niet.',
      )
    }
    throw new Error('Versturen lukte niet.')
  }
}

export interface PushHealth {
  devices: number
  expired: number
  queued: number
  lastSent: string | null
  lastError: string | null
}

export async function pushHealth(): Promise<PushHealth | null> {
  const { data, error } = await supabase.rpc('push_health')
  const row = Array.isArray(data) ? data[0] : data
  if (error || !row) return null
  return {
    devices: Number(row.toestellen ?? 0),
    expired: Number(row.verlopen ?? 0),
    queued: Number(row.in_wachtrij ?? 0),
    lastSent: (row.laatst_gelukt as string | null) ?? null,
    lastError: (row.laatste_fout as string | null) ?? null,
  }
}

/**
 * What the numbers mean, as a sentence.
 *
 * "It does not work" is not a diagnosis. These four cases look identical from
 * the outside and need completely different answers, so they get different
 * words.
 */
export function explainHealth(health: PushHealth | null): string | null {
  if (!health) return null
  if (health.devices === 0 && health.expired > 0) {
    return 'Je aanmelding is verlopen. Zet meldingen opnieuw aan.'
  }
  if (health.devices === 0) return null
  if (health.queued > 0 && !health.lastSent) {
    return 'Er staat iets klaar maar er is nog nooit iets afgeleverd — de worker komt er niet doorheen.'
  }
  if (health.queued > 2) {
    return `${health.queued} meldingen staan te wachten. De worker loopt achter.`
  }
  if (health.lastSent) {
    const when = new Date(health.lastSent)
    return `Laatst afgeleverd: ${when.toLocaleDateString('nl-BE', {
      day: 'numeric',
      month: 'short',
    })} om ${when.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}.`
  }
  return 'Aangemeld, maar er is nog nooit een melding afgeleverd.'
}

export function explainStatus(status: PushStatus): string {
  switch (status) {
    case 'granted':
      return 'Meldingen staan aan.'
    case 'denied':
      // Honest: iOS gives no way back from here inside the app.
      return 'Meldingen staan uit. Zet ze aan via Instellingen → Familie → Berichtgeving.'
    case 'needs_install':
      return 'Zet de app eerst op je beginscherm — anders kan iOS geen meldingen sturen.'
    case 'ios_too_old':
      return 'Meldingen werken vanaf iOS 16.4. Werk iOS bij via Instellingen.'
    case 'unsupported':
      return 'Deze browser ondersteunt geen meldingen.'
    default:
      return 'Krijg bericht wanneer er iets afgehandeld is.'
  }
}
